import type { User } from '@/api/types';
import { paidSubscriptionOf } from '@/auth/mapUser';

export interface AccessState {
  /** Полный доступ к контенту. Правило считает бэкенд (AccessPolicy). */
  hasFullAccess: boolean;
  /** Глобальный бесплатный режим включён. */
  isFreeMode: boolean;
  /** Показывать ли платный слой: баннер, Upgrade, paywall. */
  isPaywallEnabled: boolean;
  /** Сервер ответил про доступ в этой сессии страницы. */
  isAccessResolved: boolean;
}

/**
 * Единственная точка правил доступа (BILL-1). Fail-safe: неопределённость
 * трактуется в пользу платного режима — старый объект пользователя без новых
 * полей откатывается на реальную подписку и включённый платный слой.
 */
export function resolveAccess(user: User | null, isAccessResolved: boolean): AccessState {
  const hasFullAccess = !!(user?.has_full_access ?? user?.has_subscription);
  const isFreeMode = user?.free_mode === true;
  return {
    hasFullAccess,
    isFreeMode,
    isPaywallEnabled: !isFreeMode,
    isAccessResolved,
  };
}

/** Окно оплаты открывается только по подтверждённому сервером состоянию (BILL-2). */
export function canOpenPaywall(access: AccessState): boolean {
  return access.isAccessResolved && access.isPaywallEnabled;
}

/**
 * Кнопка Upgrade (NAV-3): авторизованный без подписки, не админ, платный режим
 * подтверждён сервером, не на странице биллинга. Живая платная строка Paddle
 * (past_due с прошедшим периодом, пауза) — тоже не рисуется: доступа там уже
 * нет, а второй чекаут дал бы двойное списание после дунинга.
 */
export function shouldShowUpgrade(
  access: AccessState,
  user: User | null,
  pathname: string,
): boolean {
  if (!user || user.is_admin) return false;
  if (!access.isAccessResolved || !access.isPaywallEnabled || access.hasFullAccess) return false;
  if (paidSubscriptionOf(user)) return false;
  return !/^\/billing(\/|$)/.test(pathname);
}

/** Баннер бесплатного лимита (CAT-13): те же условия, без привязки к маршруту. */
export function shouldShowFreeBanner(access: AccessState, user: User | null): boolean {
  if (!user || user.is_admin) return false;
  return access.isAccessResolved && access.isPaywallEnabled && !access.hasFullAccess;
}
